import { Fragment, useSyncExternalStore } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { ChevronRight, Menu, PanelLeft, QrCode, Search } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { useT } from '@/i18n/I18nProvider'
import { cn } from '@/lib/utils'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useSidebar,
} from '@/components/ui'
import { NotificationBell } from '@/components/NotificationBell'
import { KaproMark } from '@/components/Logo'
import { AccountMenuContent, UserAvatar } from './AccountMenu'
import { NewMenu } from './QuickActions'
import {
  breadcrumbFor,
  crumbTitlesVersion,
  subscribeCrumbTitles,
  type QuickAction,
} from './navigation'
import { shortcutLabel } from './shortcut'

/*
 * One square icon button for the header: 44px on touch, 36px with a mouse,
 * like every other control (the `control` token in index.css).
 */
const ICON_BUTTON = cn(
  'flex size-control shrink-0 items-center justify-center rounded-xl transition-colors',
  'text-foreground/80 hover:bg-muted hover:text-foreground',
  'focus-visible:ring-ring/50 focus-visible:ring-2 focus-visible:outline-none',
)

/**
 * Where you are, as the header's title.
 *
 * This is what the big "Administracija" heading and its subtitle used to say,
 * moved up into a bar that was there anyway — so the page itself can start at
 * the top of the screen. The parent links back, so leaving a report for its
 * list is in the same place on every screen.
 */
function Breadcrumb() {
  const t = useT()
  const { pathname } = useLocation()
  // Re-read when a page names its last crumb — a person's page, say.
  useSyncExternalStore(subscribeCrumbTitles, crumbTitlesVersion)
  const crumbs = breadcrumbFor(pathname)

  if (crumbs.length === 0) return null

  return (
    <nav aria-label={t('crumb.label')} className="min-w-0">
      <ol className="flex min-w-0 items-center gap-1 text-sm">
        {crumbs.map((crumb, index) => {
          const last = index === crumbs.length - 1
          const text = crumb.label ?? t(crumb.labelKey)

          return (
            <Fragment key={`${crumb.labelKey}-${index}`}>
              {index > 0 && (
                <li aria-hidden className="text-muted-foreground/60 shrink-0">
                  <ChevronRight className="size-3.5" />
                </li>
              )}
              <li className={cn('min-w-0', !last && 'shrink-0')}>
                {last ? (
                  <span
                    aria-current="page"
                    className="font-heading block truncate text-[0.9375rem] font-semibold"
                  >
                    {text}
                  </span>
                ) : crumb.to ? (
                  <Link
                    to={crumb.to}
                    className="text-muted-foreground hover:text-foreground focus-visible:ring-ring/50 rounded-sm transition-colors focus-visible:ring-2 focus-visible:outline-none"
                  >
                    {text}
                  </Link>
                ) : (
                  <span className="text-muted-foreground">{text}</span>
                )}
              </li>
            </Fragment>
          )
        })}
      </ol>
    </nav>
  )
}

/**
 * The search box, which is a button.
 *
 * It looks like a field so that it reads as "type here" — but typing happens
 * in the palette it opens, which is where the results have room to be shown.
 * The shortcut is printed inside it, which is the only way most people ever
 * learn one exists.
 */
function SearchBox({ onOpen }: { onOpen: () => void }) {
  const t = useT()

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-haspopup="dialog"
      aria-keyshortcuts="Control+K Meta+K"
      className={cn(
        'flex h-control w-full max-w-md items-center gap-2 rounded-xl px-3 text-sm',
        'bg-muted/60 text-muted-foreground border border-transparent transition-colors',
        'hover:bg-muted hover:text-foreground',
        'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-2 focus-visible:outline-none',
      )}
    >
      <Search className="size-4 shrink-0" aria-hidden />
      <span className="flex-1 truncate text-left">{t('search.open')}</span>
      <kbd className="bg-background text-muted-foreground hidden rounded-md border px-1.5 py-0.5 font-sans text-[0.6875rem] font-medium sm:inline">
        {shortcutLabel()}
      </kbd>
    </button>
  )
}

function SidebarToggle() {
  const t = useT()
  const { toggleSidebar, isMobile, openMobile } = useSidebar()

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <button
            type="button"
            onClick={toggleSidebar}
            aria-label={t(isMobile ? 'header.menu' : 'header.toggleSidebar')}
            aria-expanded={isMobile ? openMobile : undefined}
            className={cn(ICON_BUTTON, '-ml-1.5')}
          />
        }
      >
        {/* A hamburger on a phone, where it opens the whole menu; the panel
            icon at a desk, where it only folds the rail to icons. */}
        {isMobile ? (
          <Menu className="size-5" aria-hidden />
        ) : (
          <PanelLeft className="size-4.5" aria-hidden />
        )}
      </TooltipTrigger>
      <TooltipContent side="bottom">
        {t(isMobile ? 'header.menu' : 'header.toggleSidebar')}
        {!isMobile && <span className="opacity-70">{shortcutLabel('B')}</span>}
      </TooltipContent>
    </Tooltip>
  )
}

export function AppHeader({
  actions,
  onSearch,
}: {
  actions: QuickAction[]
  onSearch: () => void
}) {
  const t = useT()
  const { user } = useAuth()
  const navigate = useNavigate()

  return (
    /*
     * Translucent, like an iOS navigation bar — content scrolls *under* it
     * rather than away from it, which is what makes the blur worth having.
     * Opaque fallback first, since `backdrop-filter` on a transparent bar
     * with no support gives unreadable text over content.
     */
    <header
      className={cn(
        'flex h-(--shell-header) items-center gap-2 px-3 lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(16rem,28rem)_minmax(0,1fr)] lg:gap-4 lg:px-4',
        'border-b border-black/5 dark:border-white/10',
        'bg-background/85 supports-[backdrop-filter]:bg-background/70',
        'backdrop-blur-xl backdrop-saturate-150',
      )}
    >
      {/*
        `min-w-0` on every level down to the text. Without it this block
        refuses to shrink below its content and the controls on the right
        overlap it — which is what a phone once showed: the bell sitting on
        top of the product name and a Bosnian surname running under it.
      */}
      <div className="flex min-w-0 flex-1 items-center gap-1.5">
        <SidebarToggle />

        {/* A phone has no rail, so the mark and whose account this is stay
            here. On a shared phone the name is the fact that changes. */}
        <span className="flex min-w-0 items-center gap-2 lg:hidden">
          <KaproMark className="size-7 shrink-0" />
          <span className="muted min-w-0 truncate text-xs">{user?.name}</span>
        </span>

        <span className="hidden min-w-0 lg:block">
          <Breadcrumb />
        </span>
      </div>

      <div className="hidden justify-center lg:flex">
        <SearchBox onOpen={onSearch} />
      </div>

      {/* `shrink-0`: the controls keep their width and the name on the left
          gives way, rather than both being squeezed into overlap. */}
      <div className="flex shrink-0 items-center justify-end gap-0.5 lg:gap-1.5">
        <Tooltip>
          <TooltipTrigger
            render={
              <button
                type="button"
                onClick={onSearch}
                aria-label={t('search.title')}
                aria-haspopup="dialog"
                className={cn(ICON_BUTTON, 'lg:hidden')}
              />
            }
          >
            <Search className="size-5" aria-hidden />
          </TooltipTrigger>
          <TooltipContent side="bottom">{t('search.title')}</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger
            render={
              <button
                type="button"
                onClick={() => navigate('/skeniraj')}
                aria-label={t('header.scanQr')}
                className={cn(ICON_BUTTON, 'lg:hidden')}
              />
            }
          >
            <QrCode className="size-5" aria-hidden />
          </TooltipTrigger>
          <TooltipContent side="bottom">{t('header.scanQr.hint')}</TooltipContent>
        </Tooltip>

        <span className="hidden lg:block">
          <NewMenu actions={actions} />
        </span>

        <NotificationBell />

        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <button
                type="button"
                className={cn(ICON_BUTTON, 'rounded-full')}
                aria-label={t('account.ariaLabel', { name: user?.name ?? '' })}
              />
            }
          >
            <UserAvatar className="pointer-fine:size-7 size-8" />
          </DropdownMenuTrigger>
          <AccountMenuContent side="bottom" align="end" />
        </DropdownMenu>
      </div>
    </header>
  )
}
